import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { offersService } from '../../../../services/offersService';
import Loading from '../../../shared/ui/Loading';
import { toast } from 'react-hot-toast';
import OfferModal from './OfferModal';

const AvailableRequests = forwardRef((props, ref) => {
  const { onOfferSuccess } = props;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    count: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: ''
  });
  
  // Modal state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);

  const fetchRequests = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        ...filters
      };
      
      console.log('Fetching available requests with params:', params); // Debug log
      const response = await offersService.getAvailableRequests(params);
      console.log('Available requests response:', response); // Debug log
      
      setRequests(response.results || response || []);
      setPagination({
        page,
        totalPages: Math.ceil((response.count || response.length || 0) / 20),
        count: response.count || response.length || 0
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching available requests:', err); // Debug log
      setError('Talepler yüklenirken bir hata oluştu');
      toast.error('Talepler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check token on component mount
    offersService.checkToken();
    fetchRequests();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleOfferClick = (request) => {
    setSelectedRequest(request);
    setShowOfferModal(true);
  };

  const handleOfferSuccess = () => {
    // Refresh the requests list after successful offer
    fetchRequests(pagination.page);
    setShowOfferModal(false);
    
    // Call parent callback if provided
    if (onOfferSuccess) {
      onOfferSuccess();
    }
  };

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => fetchRequests(pagination.page)
  }));

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading && requests.length === 0) {
    return (
      <div className="text-center p-4">
        <Loading />
      </div>
    );
  }

  return (
    <div>
      {/* Search */}
      <div className="box m-b">
        <div className="box-header">
          <h3>Arama</h3>
        </div>
        <div className="box-body">
          <div className="row">
            <div className="col-sm-8 col-sm-offset-2 m-b-sm">
              <input
                type="text"
                className="form-control"
                placeholder="Arama yapın..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      {error ? (
        <div className="alert alert-danger">
          {error}
        </div>
      ) : (
        <>
          <div className="text-muted m-b">
            Toplam {pagination.count} talep bulundu
          </div>
          
          {requests.length === 0 ? (
            <div className="box text-center p-4">
              <i className="fa fa-search fa-3x text-muted m-b"></i>
              <h4>
                Henüz açık talep bulunmuyor
              </h4>
              <p className="text-muted">
                Filtreleri değiştirerek yeni talepler arayabilirsiniz.
              </p>
            </div>
          ) : (
            <div className="row">
              {requests.map((request) => (
                <div key={request.id} className="col-sm-6 col-lg-4 m-b">
                  <div className="box">
                    <div className="box-header">
                      <div className="pull-right">
                        <span className="label label-primary">
                          {request.organization_type}
                        </span>
                      </div>
                      <h5 className="m-0" style={{ paddingRight: '80px' }}>
                        {request.musician_type}
                      </h5>
                    </div>
                    <div className="box-body">
                      <div className="m-b-sm">
                        <i className="fa fa-map-marker text-muted m-r-xs"></i>
                        <small>{request.city} / {request.town}</small>
                      </div>
                      
                      <p className="text-sm" style={{ minHeight: '60px' }}>
                        {request.description && request.description.length > 100 
                          ? request.description.substring(0, 100) + '...'
                          : request.description || 'Açıklama bulunmuyor'
                        }
                      </p>
                      
                      <div className="m-b-sm">
                        <strong>Talep Sahibi:</strong> {request.requester_name || 'Belirtilmemiş'}
                      </div>
                      
                      <div className="row m-b-sm">
                        <div className="col-xs-6">
                          <small className="text-muted">
                            <i className="fa fa-calendar m-r-xs"></i>
                            {formatDate(request.create_time)}
                          </small>
                        </div>
                        <div className="col-xs-6 text-right">
                          <small className="text-muted">
                            <i className="fa fa-users m-r-xs"></i>
                            {request.offers_count || 0} teklif
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="box-footer">
                      <button 
                        className="btn btn-sm btn-primary btn-block"
                        onClick={() => handleOfferClick(request)}
                      >
                        <i className="fa fa-plus m-r-xs"></i>
                        Teklif Ver
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="text-center m-t">
              <div className="btn-group">
                <button 
                  className="btn btn-default"
                  disabled={pagination.page === 1}
                  onClick={() => fetchRequests(pagination.page - 1)}
                >
                  <i className="fa fa-chevron-left"></i>
                </button>
                
                <span className="btn btn-default">
                  {pagination.page} / {pagination.totalPages}
                </span>
                
                <button 
                  className="btn btn-default"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => fetchRequests(pagination.page + 1)}
                >
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Offer Modal */}
      {showOfferModal && (
        <OfferModal
          request={selectedRequest}
          onClose={() => setShowOfferModal(false)}
          onSuccess={handleOfferSuccess}
        />
      )}
    </div>
  );
});

export default AvailableRequests; 